/**
 * Design Template Cache Service
 * Caches design JSON extracted from PitchPrint runtime after initial load
 * Uses collectionPage + shape as cache key since that's the v3.14 identifier
 *
 * @version 1.0.0
 * @requires PitchPrint editor to be loaded for extraction
 */
(function() {
  'use strict';

  var DEBUG = false;
  var log = function() {
    if (DEBUG) console.log.apply(console, ['[TemplateCache]'].concat(Array.prototype.slice.call(arguments)));
  };

  // Cache configuration
  var CACHE_KEY = 'popsigns-design-templates';
  var CACHE_VERSION = '1.0';
  var CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days
  var MAX_CACHED_DESIGNS = 10; // Limit to prevent quota issues

  var DesignTemplateCache = {
    // In-memory cache for current session (faster access)
    _memoryCache: {},

    /**
     * Get cached template JSON
     * v3.15.14: Added optional title validation to prevent wrong template being returned
     * @param {number} collectionPage - Collection page number (1-15)
     * @param {string} shape - Shape type ('classic', 'circle', etc.)
     * @param {string} [expectedTitle] - Optional template title to validate against
     * @returns {Object|null} Cached design JSON or null if not found/mismatched
     */
    get: function(collectionPage, shape, expectedTitle) {
      var cacheKey = this._buildKey(collectionPage, shape);

      // Check memory first (fastest)
      if (this._memoryCache[cacheKey]) {
        var memEntry = this._memoryCache[cacheKey];
        // v3.15.14: Validate title if provided
        if (expectedTitle && memEntry._cachedTitle && memEntry._cachedTitle !== expectedTitle) {
          log('Cache title mismatch (memory):', cacheKey, 'expected:', expectedTitle, 'got:', memEntry._cachedTitle);
          return null;
        }
        log('Cache hit (memory):', cacheKey);
        return JSON.parse(JSON.stringify(memEntry)); // Return deep clone
      }

      // Check localStorage
      try {
        var stored = localStorage.getItem(CACHE_KEY);
        if (stored) {
          var cache = JSON.parse(stored);
          if (cache.version === CACHE_VERSION && cache.templates && cache.templates[cacheKey]) {
            var entry = cache.templates[cacheKey];
            // Check if not expired
            if (Date.now() - entry.timestamp < CACHE_TTL) {
              // v3.15.14: Validate title if provided
              if (expectedTitle && entry.title && entry.title !== expectedTitle) {
                log('Cache title mismatch (localStorage):', cacheKey, 'expected:', expectedTitle, 'got:', entry.title);
                return null;
              }
              // Populate memory cache
              this._memoryCache[cacheKey] = entry.data;
              log('Cache hit (localStorage):', cacheKey);
              return JSON.parse(JSON.stringify(entry.data)); // Return deep clone
            } else {
              log('Cache expired:', cacheKey);
            }
          }
        }
      } catch (e) {
        log('Cache read error:', e);
      }

      log('Cache miss:', cacheKey);
      return null;
    },

    /**
     * Store template JSON in cache
     * v3.15.14: Now stores title for validation
     * @param {number} collectionPage - Collection page number (1-15)
     * @param {string} shape - Shape type ('classic', 'circle', etc.)
     * @param {Object} designJson - Full design JSON from runtime
     * @param {string} [title] - Optional template title for validation
     */
    set: function(collectionPage, shape, designJson, title) {
      if (!designJson) {
        log('Attempted to cache empty design');
        return;
      }

      var cacheKey = this._buildKey(collectionPage, shape);

      // v3.15.14: Store title with the design for validation
      if (title) {
        designJson._cachedTitle = title;
      }

      // Store in memory cache
      this._memoryCache[cacheKey] = designJson;
      log('Cached to memory:', cacheKey, title ? '(title: ' + title + ')' : '');

      // Store in localStorage
      try {
        var stored = localStorage.getItem(CACHE_KEY);
        var cache = stored ? JSON.parse(stored) : { version: CACHE_VERSION, templates: {} };

        // Ensure cache structure is valid
        if (cache.version !== CACHE_VERSION) {
          cache = { version: CACHE_VERSION, templates: {} };
        }
        if (!cache.templates) {
          cache.templates = {};
        }

        // Add new entry with title for validation
        cache.templates[cacheKey] = {
          data: designJson,
          timestamp: Date.now(),
          title: title || null
        };

        // Enforce max cache size (LRU eviction)
        var keys = Object.keys(cache.templates);
        if (keys.length > MAX_CACHED_DESIGNS) {
          // Sort by timestamp, remove oldest
          keys.sort(function(a, b) {
            return cache.templates[a].timestamp - cache.templates[b].timestamp;
          });
          var toRemove = keys.slice(0, keys.length - MAX_CACHED_DESIGNS);
          toRemove.forEach(function(key) {
            delete cache.templates[key];
            log('Evicted from cache:', key);
          });
        }

        // Check size before storing
        var jsonString = JSON.stringify(cache);
        if (jsonString.length > 4 * 1024 * 1024) { // 4MB limit (localStorage typically 5MB)
          log('Cache too large, clearing old entries');
          this._clearOldest(cache);
          jsonString = JSON.stringify(cache);
        }

        localStorage.setItem(CACHE_KEY, jsonString);
        log('Cached to localStorage:', cacheKey, '(' + Math.round(jsonString.length / 1024) + 'KB)');
      } catch (e) {
        log('Cache write error:', e);
        // If quota exceeded, clear cache and try again
        if (e.name === 'QuotaExceededError') {
          this.clear();
          try {
            var newCache = {
              version: CACHE_VERSION,
              templates: {}
            };
            newCache.templates[cacheKey] = {
              data: designJson,
              timestamp: Date.now()
            };
            localStorage.setItem(CACHE_KEY, JSON.stringify(newCache));
            log('Stored after clearing cache');
          } catch (e2) {
            log('Failed to store even after clearing:', e2);
          }
        }
      }
    },

    /**
     * Extract design JSON from PitchPrint runtime
     * Call this after a design has been loaded/swapped
     * @returns {Object|null} Deep cloned design pages or null if unavailable
     */
    extractFromRuntime: function() {
      if (!window.app || !window.app.model || !window.app.model.runtime) {
        log('PitchPrint runtime not available');
        return null;
      }

      var runtime = window.app.model.runtime;

      // Check for pages
      if (!runtime.pages || runtime.pages.length === 0) {
        log('No pages in runtime');
        return null;
      }

      try {
        // Deep clone to avoid mutations affecting the live editor
        var designData = {
          pages: JSON.parse(JSON.stringify(runtime.pages)),
          // Include additional design metadata that might be useful
          designId: runtime.design ? runtime.design.id : null,
          title: runtime.design ? runtime.design.title : null,
          unit: runtime.design ? runtime.design.unit : 'in',
          dpi: runtime.design ? runtime.design.dpiValue : 150
        };

        log('Extracted design from runtime:', designData.pages.length, 'pages');
        return designData;
      } catch (e) {
        log('Error extracting from runtime:', e);
        return null;
      }
    },

    /**
     * Extract specific page design elements for analysis
     * @param {number} pageIndex - Page index (0-based)
     * @returns {Object|null} Page element summary
     */
    extractPageElements: function(pageIndex) {
      var designData = this.extractFromRuntime();
      if (!designData || !designData.pages[pageIndex]) {
        return null;
      }

      var page = designData.pages[pageIndex];
      var canvas = page.canvas || page;
      var objects = canvas._objects || canvas.objects || [];

      var elements = {
        backgrounds: [],
        textObjects: [],
        imageObjects: [],
        otherObjects: []
      };

      objects.forEach(function(obj, index) {
        var elementInfo = {
          index: index,
          type: obj.type,
          name: obj.name || obj.id || null,
          left: obj.left,
          top: obj.top,
          width: obj.width,
          height: obj.height
        };

        // Categorize by type
        if (obj.type === 'rect') {
          // Check if it's a background
          var name = (obj.name || obj.id || '').toLowerCase();
          if (name.includes('background') || name.includes('bg') ||
              (obj.left <= 0 && obj.top <= 0 && obj.width > 500)) {
            elementInfo.fill = obj.fill;
            elements.backgrounds.push(elementInfo);
          } else {
            elements.otherObjects.push(elementInfo);
          }
        } else if (['i-text', 'text', 'textbox'].includes(obj.type)) {
          elementInfo.text = obj.text;
          elementInfo.fontSize = obj.fontSize;
          elementInfo.fontFamily = obj.fontFamily;
          elementInfo.fill = obj.fill;
          elements.textObjects.push(elementInfo);
        } else if (obj.type === 'image') {
          elementInfo.src = obj.src;
          var imgName = (obj.name || obj.id || '').toLowerCase();
          elementInfo.isLogo = imgName.includes('logo') || imgName.includes('placeholder');
          elements.imageObjects.push(elementInfo);
        } else {
          elements.otherObjects.push(elementInfo);
        }
      });

      // Sort text objects by vertical position (top to bottom)
      elements.textObjects.sort(function(a, b) {
        return (a.top || 0) - (b.top || 0);
      });

      log('Page', pageIndex, 'elements:',
          elements.backgrounds.length, 'bgs,',
          elements.textObjects.length, 'text,',
          elements.imageObjects.length, 'images');

      return elements;
    },

    /**
     * Check if we have a cached design for the given template
     * @param {number} collectionPage - Collection page number
     * @param {string} shape - Shape type
     * @returns {boolean}
     */
    has: function(collectionPage, shape) {
      return this.get(collectionPage, shape) !== null;
    },

    /**
     * Clear all cached designs
     */
    clear: function() {
      this._memoryCache = {};
      try {
        localStorage.removeItem(CACHE_KEY);
        log('Cache cleared');
      } catch (e) {
        log('Error clearing cache:', e);
      }
    },

    /**
     * Clear expired entries only
     */
    cleanup: function() {
      try {
        var stored = localStorage.getItem(CACHE_KEY);
        if (!stored) return;

        var cache = JSON.parse(stored);
        if (!cache.templates) return;

        var now = Date.now();
        var cleaned = { version: CACHE_VERSION, templates: {} };
        var removedCount = 0;

        Object.keys(cache.templates).forEach(function(key) {
          if (now - cache.templates[key].timestamp < CACHE_TTL) {
            cleaned.templates[key] = cache.templates[key];
          } else {
            removedCount++;
          }
        });

        if (removedCount > 0) {
          localStorage.setItem(CACHE_KEY, JSON.stringify(cleaned));
          log('Cleanup removed', removedCount, 'expired entries');
        }
      } catch (e) {
        log('Cleanup error:', e);
      }
    },

    /**
     * Get cache statistics
     * @returns {Object} Cache stats
     */
    getStats: function() {
      var stats = {
        memoryCount: Object.keys(this._memoryCache).length,
        storageCount: 0,
        storageSize: 0,
        oldestEntry: null,
        newestEntry: null
      };

      try {
        var stored = localStorage.getItem(CACHE_KEY);
        if (stored) {
          stats.storageSize = stored.length;
          var cache = JSON.parse(stored);
          if (cache.templates) {
            var keys = Object.keys(cache.templates);
            stats.storageCount = keys.length;

            if (keys.length > 0) {
              var timestamps = keys.map(function(k) { return cache.templates[k].timestamp; });
              stats.oldestEntry = new Date(Math.min.apply(null, timestamps)).toISOString();
              stats.newestEntry = new Date(Math.max.apply(null, timestamps)).toISOString();
            }
          }
        }
      } catch (e) {
        log('Error getting stats:', e);
      }

      return stats;
    },

    // Private helpers

    /**
     * Build cache key from collectionPage and shape
     */
    _buildKey: function(collectionPage, shape) {
      return (shape || 'classic') + '-' + collectionPage;
    },

    /**
     * Clear oldest entries to make room
     */
    _clearOldest: function(cache) {
      if (!cache.templates) return;

      var keys = Object.keys(cache.templates);
      if (keys.length <= 1) return;

      // Sort by timestamp, remove oldest half
      keys.sort(function(a, b) {
        return cache.templates[a].timestamp - cache.templates[b].timestamp;
      });

      var toRemove = keys.slice(0, Math.ceil(keys.length / 2));
      toRemove.forEach(function(key) {
        delete cache.templates[key];
      });

      log('Cleared', toRemove.length, 'oldest entries');
    },

    /**
     * Enable/disable debug logging
     */
    setDebug: function(enabled) {
      DEBUG = !!enabled;
    }
  };

  // Run cleanup on load
  DesignTemplateCache.cleanup();

  // Expose globally
  window.DesignTemplateCache = DesignTemplateCache;
  log('Design Template Cache initialized');
})();
